/**
 * Hook to manage locale configuration for charts
 * Applies locale to d3-time-format globally
 */

import { useEffect } from "react";
import { timeFormatDefaultLocale } from "d3-time-format";
import { useSettingsStore } from "../store/settingsStore";
import { getLocale } from "../utils/locales";

/**
 * Hook that applies the current locale to d3-time-format
 * Should be called once in the app root or in ChartCanvas
 */
export function useChartLocale() {
  const locale = useSettingsStore((state) => state.locale);

  useEffect(() => {
    const localeDefinition = getLocale(locale);
    timeFormatDefaultLocale(localeDefinition);
  }, [locale]);

  return locale;
}

/**
 * Get formatters for the current locale
 * Returns format strings compatible with d3-time-format
 * The locale parameter is unused because d3-time-format uses the global locale
 */
export function getLocaleFormatters() {
  // These format strings use d3-time-format directives
  // They will automatically use the locale set by timeFormatDefaultLocale
  return {
    yearFormat: "%Y",
    quarterFormat: "%b %Y", // Short month + year (e.g., "Jan 2023" or "Jan 2023")
    monthFormat: "%b", // Short month (e.g., "Jan" or "Jan")
    weekFormat: "%d %b", // Day + short month (e.g., "15 Jan")
    dayFormat: "%d %b", // Day + short month (e.g., "15 Jan") — unified with weekFormat to avoid weekday outlier on daily charts (#307)
    hourFormat: "%H:%M", // Hour:minute in 24h format (e.g., "17:00") — no AM/PM, chart is 24h (#307)
    minuteFormat: "%H:%M", // Hour:minute in 24h format (e.g., "10:30")
    secondFormat: "%H:%M:%S", // Hour:minute:second (e.g., "10:30:45")
    milliSecondFormat: "%L", // Milliseconds (e.g., "123")
  };
}
